// src/api/contract-api.js - Contract API Helper
const getToken = () => localStorage.getItem('authToken');
const getBaseUrl = () => (process.env.REACT_APP_API_URL || '').replace('/graphql', '');

const contractApi = {
  /**
   * Download contract by filename
   */
  downloadContract: async (filename) => {
    try {
      const response = await fetch(`${getBaseUrl()}/api/contracts/download/${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download contract');
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error downloading contract:', error);
      throw error;
    }
  },

  /**
   * Upload signed contract
   */
  uploadSignedContract: async (file, contractNumber, laboratoryId, originalNotificationId) => {
    try {
      const formData = new FormData();
      formData.append('signedContract', file);
      formData.append('contractNumber', contractNumber);
      formData.append('laboratoryId', laboratoryId);
      formData.append('originalNotificationId', originalNotificationId);

      const response = await fetch(`${getBaseUrl()}/api/contracts/upload-signed`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Upload failed');
      }

      return result;
    } catch (error) {
      console.error('Error uploading signed contract:', error);
      throw error;
    }
  },

  /**
   * Get signed contract information
   */
  getSignedContractInfo: async (contractNumber) => {
    try {
      const response = await fetch(`${getBaseUrl()}/api/contracts/signed-info/${contractNumber}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get contract info');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting signed contract info:', error);
      throw error;
    }
  },

  /**
   * Download signed contract (for admins)
   */
  downloadSignedContract: async (contractNumber) => {
    try {
      // First get the contract info to find the actual signed file
      const contractInfo = await contractApi.getSignedContractInfo(contractNumber);
      
      if (!contractInfo.signedFiles || contractInfo.signedFiles.length === 0) {
        throw new Error('No signed contract found');
      }

      // Get the most recent signed file
      const latestSignedFile = contractInfo.signedFiles.sort((a, b) => 
        new Date(b.uploadDate) - new Date(a.uploadDate)
      )[0];

      const response = await fetch(`${getBaseUrl()}/api/contracts/signed/${latestSignedFile.filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download signed contract');
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = latestSignedFile.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error downloading signed contract:', error);
      throw error;
    }
  },

  /**
   * View contract in new tab
   */
  viewContract: (filename) => {
    const url = `${getBaseUrl()}/api/contracts/view/${filename}`;
    window.open(url, '_blank');
  }
};

export default contractApi